// src/pages/public/AboutPage.jsx
import React, { useEffect, useState } from "react";
import { db } from "../../config/firebase";
import { collection, doc, onSnapshot, orderBy, query } from "firebase/firestore";

/* ---------- simple mobile hook (same pattern as other pages) ---------- */
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= breakpoint : false
  );

  useEffect(() => {
    const handler = () => {
      if (typeof window === "undefined") return;
      setIsMobile(window.innerWidth <= breakpoint);
    };
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [breakpoint]);

  return isMobile;
}

/* ---------- Basic layout pieces (same look you used) ---------- */
const Screen = ({ children }) => (
  <div
    style={{
      backgroundColor: "#ffffff",
      minHeight: "100vh",
      fontFamily:
        "Work Sans, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
    }}
  >
    {children}
  </div>
);

const Container = ({ children, isMobile }) => (
  <div
    style={{
      maxWidth: 1200,
      margin: "0 auto",
      padding: isMobile ? "28px 16px 40px" : "40px 20px",
    }}
  >
    {children}
  </div>
);

const Heading = ({ children, size = 35 }) => (
  <div
    style={{
      color: "#101827",
      fontSize: size,
      fontWeight: 700,
      lineHeight: size >= 34 ? "50px" : "53px",
      marginBottom: size >= 34 ? 8 : 12,
    }}
  >
    {children}
  </div>
);

const Paragraph = ({ children, size = 16, weight = 500 }) => (
  <div
    style={{
      color: "#6b7280",
      fontSize: size,
      fontWeight: weight,
      lineHeight: size >= 16 ? "30px" : "28px",
      marginBottom: 14,
    }}
  >
    {children}
  </div>
);

/* ---------- Service row (no checkbox) ---------- */
function ServiceRow({ name, durationMin, price, from }) {
  return (
    <div style={sx.card}>
      <div style={sx.cardLeft}>
        <div style={sx.cardTitle}>{name}</div>
        <div style={sx.cardMeta}>{formatDuration(durationMin)}</div>
      </div>

      <div style={sx.priceWrap}>
        <div style={sx.pricePill}>{formatCAD(price)}</div>
        {from && <div style={sx.fromText}>From</div>}
      </div>
    </div>
  );
}

/* ---------- Helpers ---------- */
function formatDuration(min) {
  if (typeof min !== "number") return "—";
  return `${min} min`;
}
function formatCAD(n) {
  if (typeof n !== "number") return "—";
  try {
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: "CAD",
      maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return `C$ ${n}`;
  }
}

/* ============================================================= */

export default function AboutPage() {
  const isMobile = useIsMobile();

  // Firestore state
  const [categories, setCategories] = useState([]); // [{id,name}]
  const [activeCatId, setActiveCatId] = useState(null);
  const [activeCatName, setActiveCatName] = useState("");
  const [services, setServices] = useState([]); // [{id,name,price,durationMin,type}]
  const [loadingCats, setLoadingCats] = useState(true);
  const [loadingSvcs, setLoadingSvcs] = useState(true);

  // Fetch categories live
  useEffect(() => {
    const qCats = query(collection(db, "categories"), orderBy("name", "asc"));
    const unsub = onSnapshot(
      qCats,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
        setCategories(rows);
        setLoadingCats(false);
        if (!activeCatId && rows.length) {
          setActiveCatId(rows[0].id);
          setActiveCatName(rows[0].name || "Category");
        }
      },
      (err) => {
        console.error("categories onSnapshot error:", err);
        setLoadingCats(false);
      }
    );
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch services of selected category live
  useEffect(() => {
    if (!activeCatId) return;
    setLoadingSvcs(true);
    const catRef = doc(db, "categories", activeCatId);
    const qSvcs = query(collection(catRef, "services"), orderBy("name", "asc"));
    const unsub = onSnapshot(
      qSvcs,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
        setServices(rows);
        setLoadingSvcs(false);
      },
      (err) => {
        console.error("services onSnapshot error:", err);
        setLoadingSvcs(false);
      }
    );
    return () => unsub();
  }, [activeCatId]);

  // Category click
  const onPickCategory = (c) => {
    setActiveCatId(c.id);
    setActiveCatName(c.name || "Category");
  };

  const gridStyle = isMobile ? sx.gridMobile : sx.grid;

  return (
    <Screen>
      <Container isMobile={isMobile}>
        {/* About content */}
        <Heading>About Us</Heading>
        <Paragraph>
          Step into 613Nail Hub, nestled in the heart of Ottawa at 598 Rideau St, 
          ,K1N 6C6, where beauty and self-care meet artistry. Our space is designed
          to make every visit feel like a moment of indulgence, turning everyday
          nail care into a luxurious experience.
        </Paragraph>
        <Paragraph>
          At 613Nail, we believe your nails tell a story — bold, elegant, or playful —
          and our talented team is here to bring that story to life. From stunning
          acrylic sets and flawless gel finishes to soothing manicures, pedicures,
          and custom designs, we specialize in transforming ideas into looks that
          shine with personality and precision.
        </Paragraph>
        <Paragraph size={15}>
          Booking your appointment is effortless with our online system. No phone
          calls, no waiting on hold — just a few clicks to reserve the time that
          works best for you. Let 613Nail Hub be your go-to destination for beauty,
          relaxation, and a little bit of glam.
        </Paragraph>

        {/* Services (Firestore-driven, categories left / services right) */}
        <Heading size={34}>Services</Heading>

        <div style={gridStyle}>
          {/* Left: Categories */}
          <div style={sx.catPanel}>
            {loadingCats && (
              <div style={sx.loadingText}>Loading categories…</div>
            )}
            {!loadingCats && categories.length === 0 && (
              <div style={sx.emptyText}>No categories yet</div>
            )}
            {categories.map((c) => {
              const active = c.id === activeCatId;
              return (
                <button
                  key={c.id}
                  onClick={() => onPickCategory(c)}
                  style={{
                    ...sx.catItem,
                    ...(active ? sx.catItemActive : {}),
                  }}
                >
                  {c.name || "Category"}
                </button>
              );
            })}
          </div>

          {/* Right: Services for selected category */}
          <div style={{ display: "grid", gap: 12 }}>
            <div style={sx.catHeading}>
              {activeCatName || (loadingCats ? "…" : "Select a category")}
            </div>

            {loadingSvcs && (
              <div style={sx.loadingText}>Loading services…</div>
            )}
            {!loadingSvcs && services.length === 0 && (
              <div style={sx.emptyText}>No services in this category</div>
            )}

            {!loadingSvcs &&
              services.map((s) => (
                <ServiceRow
                  key={s.id}
                  name={s.name}
                  durationMin={s.durationMin}
                  price={s.price}
                  from={s.type?.toLowerCase() === "from"}
                />
              ))}
          </div>
        </div>
      </Container>
    </Screen>
  );
}

/* ---------- Styles for the services section ---------- */
const sx = {
  grid: {
    display: "grid",
    gridTemplateColumns: "280px 1fr",
    gap: 24,
    alignItems: "start",
    marginTop: 16,
  },
  gridMobile: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 16,
    alignItems: "start",
    marginTop: 16,
  },

  /* Left panel */
  catPanel: {
    background: "#ffffff",
    borderRadius: 8,
    border: "1px solid #e5e7eb",
    padding: 14,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  catItem: {
    textAlign: "left",
    background: "transparent",
    border: 0,
    padding: "10px 12px",
    borderRadius: 6,
    fontSize: 14,
    fontWeight: 700,
    color: "#030303",
    cursor: "pointer",
  },
  catItemActive: {
    background: "#eee9f2",
    boxShadow: "inset 0 0 0 1px #d7d1dc",
  },

  /* Service rows (right) */
  catHeading: { fontSize: 16, fontWeight: 700, color: "#030303" },
  card: {
    background: "#ffffff",
    borderRadius: 8,
    border: "1px solid #e5e7eb",
    padding: 14,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  cardLeft: { display: "grid", gap: 4 },
  cardTitle: {
    color: "#5c5470",
    fontSize: 16,
    fontWeight: 600,
    lineHeight: "22px",
  },
  cardMeta: {
    color: "#6b7280",
    fontSize: 14,
    fontWeight: 600,
    lineHeight: "22px",
  },
  priceWrap: {
    display: "grid",
    justifyItems: "end",
    alignContent: "center",
    gap: 4,
    minWidth: 90,
  },
  pricePill: {
    background: "#5c5470",
    color: "#fff",
    fontSize: 14,
    fontWeight: 700,
    padding: "6px 10px",
    borderRadius: 20,
    minWidth: 68,
    textAlign: "center",
  },
  fromText: { color: "#6b7280", fontSize: 12, fontWeight: 600 },

  loadingText: { color: "#6b7280", fontSize: 14 },
  emptyText: { color: "#9aa0a6", fontSize: 14, fontStyle: "italic" },
};
